/**
 * ListView only reports the rows it currently displays, so using its report as-is drops the
 * selections made on the rows it does not display (other pages, rows hidden by a filter). Merge the
 * reported row ids with the currently-selected ones that are out of view.
 *
 * @param {Array<Object>} reportedItems - The rows reported by ListView through setSelectedItems
 * @param {Array<string>} visibleIds - The ids of the rows currently handed over to ListView
 * @param {Array<string>} currentSelectedIds - The ids selected so far
 *
 * @returns {Array<string>} The merged selected ids
 */
export const mergeReportedSelection = (reportedItems, visibleIds, currentSelectedIds) => {
  const reportedIds = (reportedItems || []).map(({ id }) => id);
  const visibleIdSet = new Set(visibleIds);

  const preservedIds = currentSelectedIds.filter((id) => !visibleIdSet.has(id));

  return [...preservedIds, ...reportedIds];
};

/**
 * Whether two id lists hold the same ids, whatever their order. Used to skip the no-op state
 * updates ListView triggers whenever it re-derives its selection from the rows it receives.
 *
 * @param {Array<string>} ids - The first id list
 * @param {Array<string>} otherIds - The id list to compare it with
 *
 * @returns {boolean}
 */
export const haveSameIds = (ids, otherIds) =>
  ids.length === otherIds.length && ids.every((id) => otherIds.includes(id));
